/**
 * pages/Pengeluaran.js
 * The Pengeluaran list — every one of our own costs (Biaya Lain-Lain, 2026-10-11; see
 * utils/expenses.js), in one place:
 *   - linked to a sale/purchase/transfer: typed in that transaction's own "Biaya Lain-Lain" box
 *     and changed ONLY there (Edit Transaksi). Read-only here — clicking the row (or its
 *     transaction link) opens the transaction.
 *   - standalone (e.g. parking on a quiet day), or linked to a transaction deleted since:
 *     added ("+ Tambah Pengeluaran"), changed and deleted here, via ExpenseFormModal.
 * One editing surface per cost, either way.
 *
 * Filters: date range (presets like Riwayat Transformasi's: default "Bulan ini"), location
 * (Semua / Pusat / each branch), category. The total shown is the total of what's filtered.
 */
import React, { useMemo, useState } from "react";
import { fmtQty, fmtDateShort, today } from "../utils/idGenerators";
import { EXPENSE_CATEGORIES, sumExpenses, isStandaloneExpense } from "../utils/expenses";
import { resolveBranchName } from "../utils/branches";
import ExpenseFormModal from "../components/ExpenseFormModal";
import TransactionDetailModal from "../components/TransactionDetailModal";

const TYPE_LABEL = { PENJUALAN: "Penjualan", PEMBELIAN: "Pembelian", TRANSFER: "Transfer" };

const shiftDays = (dateStr, days) => {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
};

/** "Penjualan 0001/IJM/10/2026" — PEMBELIAN uses the supplier's own invoice no. */
export function txnLabelFor(txn) {
  if (!txn) return "";
  const number = txn.type === "PEMBELIAN" ? txn.supplierInvoiceNo : txn.invoiceNumber;
  return `${TYPE_LABEL[txn.type] || txn.type} ${number || "—"}`;
}

export default function Pengeluaran({
  expenses = [], transactions = [], branches = [], items = [], contacts = [], salespeople = [], persons = [],
  onSave, onDelete,
}) {
  const monthStart = today().slice(0, 8) + "01";
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today());
  const [preset, setPreset] = useState("month");
  const [place, setPlace] = useState("ALL"); // "ALL" | "PUSAT" | <branch id>
  const [category, setCategory] = useState("ALL");
  const [modal, setModal] = useState(null); // { initial } | null
  const [openTxnId, setOpenTxnId] = useState(null);

  const txnById = useMemo(() => new Map(transactions.map((t) => [t.id, t])), [transactions]);

  const applyPreset = (key) => {
    setPreset(key);
    const t = today();
    if (key === "today") { setFromDate(t); setToDate(t); }
    else if (key === "7") { setFromDate(shiftDays(t, -6)); setToDate(t); }
    else if (key === "month") { setFromDate(t.slice(0, 8) + "01"); setToDate(t); }
    else { setFromDate(""); setToDate(""); }
  };

  const filtered = useMemo(() => {
    return expenses
      .filter((e) => (!fromDate || e.date >= fromDate) && (!toDate || e.date <= toDate))
      .filter((e) => place === "ALL" || (place === "PUSAT" ? e.location === "PUSAT" : e.location === "CABANG" && e.branchId === place))
      .filter((e) => category === "ALL" || e.category === category)
      .sort((a, b) => (a.date !== b.date ? (a.date < b.date ? 1 : -1) : (a.createdAt < b.createdAt ? 1 : -1)));
  }, [expenses, fromDate, toDate, place, category]);

  const total = sumExpenses(filtered);
  const placeName = (e) => (e.location === "CABANG" ? `Cabang ${resolveBranchName(branches, e.branchId) || ""}`.trim() : "Pusat");
  const openTxn = openTxnId ? txnById.get(openTxnId) : null;
  const editing = modal && modal.initial;
  const orphanNote = editing && editing.txnId && !txnById.has(editing.txnId)
    ? "Transaksi untuk pengeluaran ini sudah dihapus — pengeluarannya tetap tercatat dan bisa diubah di sini."
    : "";
  const books = { transactions };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Pengeluaran</h1>
          <div className="page-sub">Biaya yang kita bayar sendiri ke pihak lain — tidak diganti pembeli/supplier.</div>
        </div>
        {onSave && <button type="button" className="btn btn-primary" onClick={() => setModal({ initial: null })}>+ Tambah Pengeluaran</button>}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--gap-md)", alignItems: "center", marginBottom: "var(--gap-md)" }}>
        <div style={{ display: "flex", gap: "var(--gap-xs)" }}>
          {[["today", "Hari ini"], ["7", "7 hari"], ["month", "Bulan ini"], ["all", "Semua"]].map(([key, label]) => (
            <button key={key} type="button" className={"btn btn-sm" + (preset === key ? " btn-primary" : "")} onClick={() => applyPreset(key)}>{label}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "var(--gap-sm)", alignItems: "center" }}>
          <input type="date" className="form-input" style={{ width: 150 }} aria-label="Dari tanggal"
            value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPreset(""); }} />
          <span style={{ color: "var(--muted)", fontSize: 13 }}>s/d</span>
          <input type="date" className="form-input" style={{ width: 150 }} aria-label="Sampai tanggal"
            value={toDate} onChange={(e) => { setToDate(e.target.value); setPreset(""); }} />
        </div>
        <select className="form-select" style={{ width: "auto" }} aria-label="Lokasi" value={place} onChange={(e) => setPlace(e.target.value)}>
          <option value="ALL">Semua lokasi</option>
          <option value="PUSAT">Pusat</option>
          {branches.map((b) => <option key={b.id} value={b.id}>Cabang {b.name}</option>)}
        </select>
        <select className="form-select" style={{ width: "auto" }} aria-label="Kategori" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="ALL">Semua kategori</option>
          {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="alert alert--info" style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--gap-md)" }} data-testid="expense-total">
        <span>Total pengeluaran ({filtered.length})</span><b>Rp {fmtQty(total)}</b>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          {expenses.length === 0 ? "Belum ada pengeluaran." : "Tidak ada pengeluaran pada filter ini."}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Tanggal</th><th>Lokasi</th><th>Kategori</th><th>Dibayar ke</th><th>Keterangan</th><th>Transaksi</th><th className="num">Jumlah</th></tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const txn = e.txnId ? txnById.get(e.txnId) : null;
                return (
                  <tr key={e.id} onClick={() => {
                    if (!isStandaloneExpense(books, e)) setOpenTxnId(e.txnId); // linked: changed in its transaction
                    else if (onSave) setModal({ initial: e });
                  }}>
                    <td>{fmtDateShort(e.date)}</td>
                    <td>{placeName(e)}</td>
                    <td>{e.category}</td>
                    <td>{e.paidTo || "—"}</td>
                    <td>{e.note || "—"}</td>
                    <td>
                      {!e.txnId ? "—" : txn ? (
                        <button type="button" className="riwayat-link" onClick={(ev) => { ev.stopPropagation(); setOpenTxnId(txn.id); }}>{txnLabelFor(txn)}</button>
                      ) : <span style={{ color: "var(--muted)" }}>(transaksi sudah dihapus)</span>}
                    </td>
                    <td className="num">{fmtQty(e.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ExpenseFormModal
          initial={modal.initial}
          orphanNote={orphanNote}
          branches={branches}
          onSave={(fields) => onSave(fields, modal.initial ? modal.initial.id : null)}
          onDelete={modal.initial && onDelete ? () => onDelete(modal.initial.id) : undefined}
          onClose={() => setModal(null)}
        />
      )}
      {openTxn && (
        <TransactionDetailModal
          txn={openTxn} items={items} contacts={contacts} salespeople={salespeople} persons={persons}
          branches={branches} today={today()} onClose={() => setOpenTxnId(null)}
        />
      )}
    </div>
  );
}
